export const EVENTS = {
  USER: {
    AUTHENTICATED: 'user:authenticated',
    LOGGED_OUT: 'user:logged_out',
    REGISTERED: 'user:registered',
    UPDATED: 'user:updated'
  },
  LANGUAGE: { CHANGED: 'language:changed' },

  RANKINGS: {
    CHARACTERS_UPDATED: 'rankings:characters:updated',
    UPCOMING_CHARACTERS_UPDATED: 'rankings:upcoming:updated',
    PAGE_CHANGED: 'rankings:page:changed',
    LOADING_CHANGED: 'rankings:loading:changed',
    ERROR_CHANGED: 'rankings:error:changed',
    FILTERS_CHANGED: 'rankings:filters:changed',
    COMING_SOON_TOGGLED: 'rankings:coming_soon:toggled',
    CHARACTER_SELECTED: 'rankings:character:selected',
    CHARACTER_UPDATED: 'rankings:character:updated',
    FAQ_UPDATED: 'rankings:faq:updated',
    FAVORITES_UPDATED: 'rankings:favorites:updated',
    PRIVATE_UPDATED: 'rankings:private:updated',
    SCENARIOS_UPDATED: 'rankings:scenarios:updated'
  }
};

class EventBus {
  constructor() {
    this.events = new Map();
    this.onceEvents = new Map();
  }

  on(event, callback) {
    if (!this.events.has(event)) this.events.set(event, new Set());
    this.events.get(event).add(callback);
    return () => this.off(event, callback);
  }

  once(event, callback) {
    const wrapper = (data) => {
      this.off(event, wrapper);
      callback(data);
    };
    
    if (!this.onceEvents.has(event)) this.onceEvents.set(event, new Map());
    this.onceEvents.get(event).set(callback, wrapper);
    return this.on(event, wrapper);
  }

  off(event, callback) {
    if (this.events.has(event)) this.events.get(event).delete(callback);
    
    const onceMap = this.onceEvents.get(event);
    if (onceMap?.has(callback)) {
      const wrapper = onceMap.get(callback);
      this.events.get(event)?.delete(wrapper);
      onceMap.delete(callback);
    }
  }

  emit(event, data) {
    this.events.get(event)?.forEach(cb => {
      try { cb(data); } 
      catch (e) { console.error(`[EventBus] ${event}:`, e); }
    });
  }

  clear() {
    this.events.clear();
    this.onceEvents.clear();
  }

  clearEvent(event) {
    this.events.delete(event);
    this.onceEvents.delete(event);
  }

  listenerCount(event) {
    return this.events.get(event)?.size ?? 0;
  }
}

class LoadingCoordinator {
  constructor() {
    this.essentialsLoaded = false;
    this.essentialsReadyEventDispatched = false;
    this.deferredModules = new Map();
    this.loadingStates = new Map();
  }

  markEssentialsLoaded() {
    this.essentialsLoaded = true;
    if (!this.essentialsReadyEventDispatched) {
      this.essentialsReadyEventDispatched = true;
      document.dispatchEvent(new CustomEvent('app:essentials-ready'));
    }
    this.loadDeferredModules();
  }

  registerDeferredModule(name, loader, options = {}) {
    const { priority = 0, useIdleCallback = false } = options;

    this.deferredModules.set(name, {
      loader,
      priority,
      useIdleCallback,
      registered: true
    });

    if (this.essentialsLoaded) {
      this.loadModule(name);
    }
  }

  async loadModule(name) {
    const moduleConfig = this.deferredModules.get(name);
    if (!moduleConfig) return;

    if (this.loadingStates.get(name) === 'loading') return;

    this.loadingStates.set(name, 'loading');

    try {
      const loaderFn = moduleConfig.useIdleCallback
        ? () => this.loadWithIdleCallback(moduleConfig.loader)
        : moduleConfig.loader;

      await loaderFn();
      this.loadingStates.set(name, 'loaded');
      console.log(`Deferred module ${name} loaded successfully`);
    } catch (error) {
      this.loadingStates.set(name, 'error');
      console.error(`Failed to load deferred module ${name}:`, error);
    }
  }

  async loadWithIdleCallback(loader) {
    return new Promise((resolve, reject) => {
      if ('requestIdleCallback' in window) {
        requestIdleCallback(async () => {
          try {
            await loader();
            resolve();
          } catch (error) {
            reject(error);
          }
        });
      } else {
        // Fallback for browsers without requestIdleCallback
        setTimeout(async () => {
          try {
            await loader();
            resolve();
          } catch (error) {
            reject(error);
          }
        }, 100);
      }
    });
  }

  loadDeferredModules() {
    // Sort by priority (higher priority loads first)
    const sortedModules = Array.from(this.deferredModules.entries())
      .sort(([,a], [,b]) => b.priority - a.priority);

    for (const [name] of sortedModules) {
      this.loadModule(name);
    }
  }

  // Utility methods for monitoring
  getLoadingState(name) {
    return this.loadingStates.get(name) || 'not-registered';
  }

  getAllLoadingStates() {
    const states = {};
    for (const [name] of this.deferredModules) {
      states[name] = this.getLoadingState(name);
    }
    return states;
  }

  isModuleLoaded(name) {
    return this.loadingStates.get(name) === 'loaded';
  }

  hasModuleFailed(name) {
    return this.loadingStates.get(name) === 'error';
  }
}

export const loadingCoordinator = new LoadingCoordinator();

export const eventBus = new EventBus();
